/**
 * SOLID Review
 * S — Good. One concern: what each server event does to the table. Nine small decisions, each the
 *     handler it replaces, side by side so their guards can be compared.
 * O — Good. A new event is a new function here and an op on the contract.
 * L — N/A — functions.
 * I — Strong. Each takes the table and its own event.
 * D — Strong. The draft helpers, the moved reveal and roster rules, and the schema types.
 */
import { GamePhase, INITIAL_LIVES } from '../../uml-components/primitives';
import { decided, type IEntityDecision, type TDecisionResult } from '../shared';
import { settle, freshLives, revealOf, challengerOf, type IConfigSettle } from './tableDraft';
import { diceRollFromReveal, phaseAfterLift } from '../../game/RevealTransforms';
import { buildRosterFromRaw, mergeRoster } from '../../game/Roster';
import type { IPropsClientTableInput, IStateClientTable } from '../../ports/schemas/ClientTable';
import type { IPropsClientChanges } from '../../ports/schemas/ClientChanges';
import type {
  IPayloadRoundStarted, IPayloadClaimAnnounced, IPayloadCupLifted, IPayloadDiceRolled, IPayloadRoomRoster,
  IPayloadPresence, IPayloadRoomAbandoned, IPayloadDecisionCountdown, IPayloadConnectivity,
  IEntityTableAck, IEntityConnectivity,
} from '../../ports/schemas/TableEvents';

/**
 * THE SERVER'S EVENTS, AS DECISIONS (frontend strict-ports plan, FE1, 2026-10-03). Before, each was a
 * subscription callback in a component or hook: ControlPanel (claim, lift, round, roster), GameSceneCore
 * (roll), LobbyPanel's two sync hooks (round, roster), `useRoomSubscriptions` (presence, abandoned,
 * countdown) and `useWsConnectionSync` (connectivity). Each callback's guards are kept, including the ones
 * that only existed because a subscription was mounted or not: "the roller never subscribes to claims"
 * becomes "a claim is ignored on the roller's table".
 *
 * An event the guards drop answers `applied: false` and changes nothing.
 */

type TEventDecision = TDecisionResult<IEntityDecision<IPropsClientChanges, IEntityTableAck>>;

function applied(base: IStateClientTable, next: IStateClientTable, markActivity: boolean): TEventDecision {
  const ack: IEntityTableAck = { applied: true };
  const how: IConfigSettle = { markActivity };
  return decided(settle(base, next, how), ack);
}

function ignored(): TEventDecision {
  const nothing: IPropsClientChanges = {};
  const ack: IEntityTableAck = { applied: false };
  return decided(nothing, ack);
}

const isLocalRoller = (t: IStateClientTable): boolean => t.localPlayerId !== null && t.localPlayerId === t.activeRollerId;

/**
 * A round started — the game's first, or the next after a believe, a roll-first or Play Again.
 *
 * Two handlers, chosen by phase as their components' mounting chose them. In the LOBBY (LobbyPanel's
 * `useLobbyRoundStartedSync`): roller and round when named, every seat at full lives, ROLLING. In a game
 * (ControlPanel): a new game after GAME_OVER restores the lives; every trace of the last round is cleared,
 * including the server's decider (the new round's is computed afresh) and the decision countdown (the
 * server never cancels one, so "Auto-believe in 0s" stuck around).
 */
export function decideRoundStarted(input: IPropsClientTableInput, round: IPayloadRoundStarted): TEventDecision {
  const { table } = input;
  const room = table.room;
  // Second line of defence behind the server's per-room topic (version skew mid-deploy).
  if (room === null || round.roomId !== room.roomId) return ignored();
  const next: IStateClientTable = { ...table };
  if (table.phase === GamePhase.LOBBY) {
    if (round.rollerId) next.activeRollerId = round.rollerId;
    if (round.roundId) next.activeRoundId = round.roundId;
    if (room.players.length > 0) next.playerLives = freshLives(room.players.map((p) => p.id));
    next.phase = GamePhase.ROLLING;
    return applied(table, next, true);
  }
  if (table.phase === GamePhase.GAME_OVER) next.playerLives = freshLives(room.players.map((p) => p.id));
  next.activeRoundId = round.roundId ?? null;
  next.activeRollerId = round.rollerId ?? null;
  next.minimumClaimScore = round.minimumScore ?? 0;
  next.lastClaimedScore = null;
  next.claimWasBlind = false;
  next.diceRevealed = false;
  next.lastDiceRoll = null;
  next.opponentClaim = null;
  next.revealResult = null;
  next.activeChallengerId = null;
  next.decisionCountdown = null;
  next.phase = GamePhase.ROLLING;
  return applied(table, next, true);
}

/**
 * A claim was announced. Every seat but the roller's tracks it, so every table moves to DECIDING; only
 * the challenger's shows the buttons. The SERVER'S decider is adopted, so a stale local roster can no
 * longer disenfranchise or mis-crown one. A claim for another round of this room is stale and dropped.
 */
export function decideClaimAnnounced(input: IPropsClientTableInput, claim: IPayloadClaimAnnounced): TEventDecision {
  const { table } = input;
  if (table.activeRoundId === null || claim.roundId !== table.activeRoundId) return ignored();
  if (isLocalRoller(table)) return ignored();
  const next: IStateClientTable = { ...table };
  next.lastClaimedScore = claim.claimedScore;
  next.claimWasBlind = claim.wasBlind === true;
  next.opponentClaim = { score: claim.claimedScore, label: claim.label };
  next.activeChallengerId = challengerOf(claim.challengerId);
  next.phase = GamePhase.DECIDING;
  return applied(table, next, true);
}

/**
 * The cup was lifted. The loser's lives are the table's count when the server sends it (backend
 * §28.191), and are decremented locally only for an older server's reveal. The dice go on screen for
 * every seat, and the game ends when the server says so — or, from an older server, when the local count
 * reaches zero. A lift for a round already finished must not touch the lives.
 */
export function decideCupLifted(input: IPropsClientTableInput, result: IPayloadCupLifted): TEventDecision {
  const { table } = input;
  if (table.activeRoundId === null || result.roundId !== table.activeRoundId) return ignored();
  const next: IStateClientTable = { ...table };
  const loser = result.losingPlayerId;
  const left = typeof result.loserLivesLeft === 'number'
    ? result.loserLivesLeft
    : Math.max(0, (table.playerLives[loser] ?? INITIAL_LIVES) - 1);
  next.playerLives = { ...table.playerLives, [loser]: left };
  next.minimumClaimScore = 0;
  if (result.die1 && result.die2) next.lastDiceRoll = diceRollFromReveal(result);
  next.revealResult = revealOf(result);
  next.phase = phaseAfterLift(result, left);
  return applied(table, next, true);
}

/** The roller rolled. Every other seat watches the dice being peeked at; a stale round's roll is dropped. */
export function decideDiceRolled(input: IPropsClientTableInput, ev: IPayloadDiceRolled): TEventDecision {
  const { table } = input;
  if (table.activeRoundId === null || ev.roundId !== table.activeRoundId) return ignored();
  if (isLocalRoller(table)) return ignored();
  const next: IStateClientTable = { ...table };
  next.phase = GamePhase.PEEKING;
  return applied(table, next, true);
}

/**
 * Someone joined or left. In the LOBBY every seat is rebuilt from the server's list — LobbyPanel's sync
 * ran AFTER or BEFORE ControlPanel's merge, and either way the rebuild was what stood. In a game the
 * roster is MERGED, so a late join never resets the lives and seats of the players already there.
 */
export function decideRoomUpdated(input: IPropsClientTableInput, updated: IPayloadRoomRoster): TEventDecision {
  const { table } = input;
  const room = table.room;
  if (room === null || updated.id !== room.roomId) return ignored();
  const players = table.phase === GamePhase.LOBBY
    ? buildRosterFromRaw(updated.players, table.localPlayerId)
    : mergeRoster(room.players, updated.players, table.localPlayerId);
  const next: IStateClientTable = { ...table, room: { ...room, players } };
  return applied(table, next, false);
}

/**
 * A player was suspended or resumed, in THIS room. A logged-in account keeps its id across rooms, so a
 * previous room's suspension of the same person must not badge them here — the room check stays even
 * though the server scopes the topic (§23.26).
 */
export function decidePlayerSuspended(input: IPropsClientTableInput, ev: IPayloadPresence): TEventDecision {
  const { table } = input;
  if (table.room === null || !ev.playerId || ev.roomId !== table.room.roomId) return ignored();
  if (table.suspendedPlayerIds.includes(ev.playerId)) return applied(table, table, false);
  const next: IStateClientTable = { ...table, suspendedPlayerIds: [...table.suspendedPlayerIds, ev.playerId] };
  return applied(table, next, false);
}

export function decidePlayerResumed(input: IPropsClientTableInput, ev: IPayloadPresence): TEventDecision {
  const { table } = input;
  if (table.room === null || !ev.playerId || ev.roomId !== table.room.roomId) return ignored();
  const playerId = ev.playerId;
  const next: IStateClientTable = { ...table, suspendedPlayerIds: table.suspendedPlayerIds.filter((id) => id !== playerId) };
  return applied(table, next, false);
}

/** The server closed this table: the room-ended overlay. */
export function decideRoomAbandoned(input: IPropsClientTableInput, ev: IPayloadRoomAbandoned): TEventDecision {
  const { table } = input;
  if (table.room === null || !ev.roomId || ev.roomId !== table.room.roomId) return ignored();
  const next: IStateClientTable = { ...table, roomEnded: true };
  return applied(table, next, false);
}

/** The server's auto-believe clock ticked. Taken as sent; the round-start decision clears it. */
export function decideDecisionCountdown(input: IPropsClientTableInput, ev: IPayloadDecisionCountdown): TEventDecision {
  const { table } = input;
  if (table.room === null) return ignored();
  const next: IStateClientTable = {
    ...table,
    decisionCountdown: { roundId: ev.roundId, challengerId: ev.challengerId, secondsRemaining: ev.secondsRemaining },
  };
  return applied(table, next, false);
}

type TConnectionState = IStateClientTable['connectionState'];
/**
 * The transport's states the table names as they are; any other — 'disconnected' included — reads as
 * disconnected. Frozen arrays, not a Map and a Set (strict-ports FE7): `lint:module-state` refuses a
 * module-level collection in the Domain, which runs on the far side of a worker in FE8's proof.
 */
const MAPPED_STATES: readonly TConnectionState[] = Object.freeze(['connecting', 'connected', 'reconnecting', 'failed']);
const DOWN_STATES: readonly string[] = Object.freeze(['reconnecting', 'disconnected', 'failed']);
const isMapped = (state: string): state is TConnectionState => (MAPPED_STATES as readonly string[]).includes(state);

/**
 * The websocket changed state. The LATCH is the point (`useWsConnectionSync`): a resync is owed when a
 * connection is RE-established, never when it is first established — and anything that is not a live
 * connection counts as a gap. An unknown state shows as disconnected but is not a gap. The latch lived in
 * a ref because an effect-local variable was reset by every re-render (found by the SUS-DEC e2e cells);
 * on the table it survives anything short of `resetAll`.
 */
export function decideConnectivity(
  input: IPropsClientTableInput,
  ev: IPayloadConnectivity,
): TDecisionResult<IEntityDecision<IPropsClientChanges, IEntityConnectivity>> {
  const { table } = input;
  const next: IStateClientTable = { ...table };
  next.connectionState = isMapped(ev.state) ? ev.state : 'disconnected';
  let resync = false;
  if (DOWN_STATES.includes(ev.state)) {
    next.connectionWasDown = true;
  } else if (ev.state === 'connected' && table.connectionWasDown) {
    next.connectionWasDown = false;
    resync = true;
  }
  const dto: IEntityConnectivity = { resync };
  return decided(settle(table, next), dto);
}
